import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Typography, Table, Tag, Button, Space, Spin, message, Modal,
  InputNumber, Input, Empty, Descriptions, Popconfirm, Badge, List,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  GiftOutlined, ReloadOutlined, InboxOutlined,
} from '@ant-design/icons';
import { couponRequestAPI } from '../services/api';

const { Title, Text } = Typography;

const statusConfig = {
  pending: { color: 'gold', label: '待審核' },
  approved: { color: 'green', label: '已核准' },
  rejected: { color: 'red', label: '已拒絕' },
};

export default function AdminCouponPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentReq, setCurrentReq] = useState(null);
  const [action, setAction] = useState('approve');
  const [maxScans, setMaxScans] = useState(5);
  const [adminNotes, setAdminNotes] = useState('');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await couponRequestAPI.listAll();
      setRequests(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const openModal = (req, act) => {
    setCurrentReq(req);
    setAction(act);
    setMaxScans(req.max_scans || 5);
    setAdminNotes('');
    setModalOpen(true);
  };

  const handleConfirm = async () => {
    if (!currentReq) return;
    setProcessing(currentReq.id);
    try {
      if (action === 'approve') {
        await couponRequestAPI.approve(currentReq.id, maxScans, adminNotes);
        message.success('已核准！通知 Email 已送出');
      } else {
        await couponRequestAPI.reject(currentReq.id, adminNotes);
        message.success('已拒絕');
      }
      setModalOpen(false);
      fetchRequests();
    } catch (e) {
      message.error(e.message || '操作失敗');
    }
    setProcessing(null);
  };

  const columns = [
    {
      title: 'ID', dataIndex: 'id', key: 'id', width: 60,
    },
    {
      title: '申請者', key: 'user', width: 120,
      render: (_, r) => (
        <div>
          <Text strong>{r.username}</Text>
          {r.email && <div><Text type="secondary" style={{ fontSize: 12 }}>{r.email}</Text></div>}
        </div>
      ),
    },
    {
      title: '狀態', dataIndex: 'status', key: 'status', width: 90,
      render: (s) => (
        <Tag color={statusConfig[s]?.color || 'default'}>
          {statusConfig[s]?.label || s}
        </Tag>
      ),
    },
    {
      title: '備註', dataIndex: 'notes', key: 'notes', ellipsis: true,
      render: (n) => n || '-',
    },
    {
      title: '掃描次數', key: 'scans', width: 120,
      render: (_, r) => r.status === 'approved'
        ? <Tag color="blue">{r.used_scans} / {r.max_scans}</Tag>
        : '-',
    },
    {
      title: '申請時間', dataIndex: 'created_at', key: 'time', width: 160,
      render: (t) => t ? new Date(t).toLocaleString('zh-TW') : '-',
    },
    {
      title: '管理員備註', dataIndex: 'admin_notes', key: 'anotes', width: 150, ellipsis: true,
      render: (n) => n || '-',
    },
    {
      title: '操作', key: 'action', width: 180,
      render: (_, r) => (
        <Space>
          {r.status === 'pending' && (
            <>
              <Button type="primary" size="small" icon={<CheckCircleOutlined />}
                onClick={() => openModal(r, 'approve')}>
                核准
              </Button>
              <Button danger size="small" icon={<CloseCircleOutlined />}
                onClick={() => openModal(r, 'reject')}>
                拒絕
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <Title level={3}>
        <Badge count={pendingCount} offset={[12, 0]}>
          <GiftOutlined style={{ marginRight: 8 }} />
        </Badge>
        優惠券申請管理
      </Title>

      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : requests.length === 0 ? (
          <Empty description="尚無優惠券申請記錄" />
        ) : (
          <Table
            dataSource={requests}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 20 }}
            expandable={{
              expandedRowRender: (r) => (
                <Descriptions size="small" column={2}>
                  <Descriptions.Item label="Coupon Code">{r.coupon_code || '-'}</Descriptions.Item>
                  <Descriptions.Item label="核准人">{r.approved_by || '-'}</Descriptions.Item>
                  <Descriptions.Item label="核准時間">
                    {r.approved_at ? new Date(r.approved_at).toLocaleString('zh-TW') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email 發送時間">
                    {r.email_sent_at ? new Date(r.email_sent_at).toLocaleString('zh-TW') : '-'}
                  </Descriptions.Item>
                </Descriptions>
              ),
            }}
          />
        )}
      </Card>

      {/* Approve/Reject Modal */}
      <Modal
        title={action === 'approve' ? '核准優惠券申請' : '拒絕優惠券申請'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleConfirm}
        confirmLoading={!!processing}
        okText={action === 'approve' ? '核准並發送 Email' : '確認拒絕'}
        okType={action === 'approve' ? 'primary' : 'danger'}
      >
        {currentReq && (
          <div style={{ padding: '8px 0' }}>
            <p><Text strong>申請者：</Text>{currentReq.username}</p>
            {currentReq.notes && (
              <p><Text strong>申請備註：</Text>{currentReq.notes}</p>
            )}

            {action === 'approve' && (
              <div style={{ marginTop: 16 }}>
                <Text strong>可掃描次數：</Text>
                <InputNumber
                  min={1}
                  max={100}
                  value={maxScans}
                  onChange={setMaxScans}
                  style={{ width: 120, marginLeft: 8 }}
                />
                <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                  設定此優惠券允許的 AEO Scanner 掃描次數
                </div>
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <Text strong>管理員備註（選填）：</Text>
              <Input.TextArea
                rows={3}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder={action === 'approve' ? '核准備註（選擇性）' : '拒絕原因說明'}
                style={{ marginTop: 8 }}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
